// Smart Chart tutorial subset: the dashboard slides from Refetch Data through
// Conversation-driven updates, which anchor inside the Smart Chart window.
// Shared by the "i" launchers and the first-open auto-launch in
// PrechartingModal. The launchers also append the Smart Assist slides.
import tutorialConfig from './tutorialConfig.json';
import { SMART_CHART_SMART_ASSIST_STEP_IDS } from './smartAssistSlides';

export const SMART_CHART_PAGE_ID = 'smart_chart';

export const SMART_CHART_STEP_IDS = [
  'precharting_refetch',
  'precharting_overview',
  'precharting_allergies',
  'precharting_vitals',
  'precharting_additional_context',
  'precharting_custom_prompt',
  'precharting_optional_outline',
  'precharting_custom_instructions',
  'precharting_conversation_updates',
];

// Some anchors only exist for patients with chart-history fields (manually
// added patients get an Additional-Context-only window): slides whose anchor
// is missing for the current patient are skipped.
const STEP_REQUIRED_SELECTORS = {
  precharting_refetch: '#prechart_refetch_button',
  precharting_overview: '#prechart_field_vitals',
  precharting_allergies: '#prechart_field_allergies',
  precharting_vitals: '#prechart_field_vitals',
  precharting_additional_context: '#prechart_additional_context',
};

// Only one tabpanel is mounted at a time, and the anchors above render with it,
// so buildSmartChartSection must not run until the Smart Chart panel is on
// screen — a build that lands early silently drops every slide gated on them.
const MOUNTED_ANCHOR_SELECTOR = [...new Set(Object.values(STEP_REQUIRED_SELECTORS))].join(', ');
const ANCHOR_POLL_INTERVAL_MS = 50;
const ANCHOR_WAIT_TIMEOUT_MS = 3000;

/**
 * Waits for the Smart Chart tabpanel and at least one of the anchors
 * buildSmartChartSection probes, so the built section is complete.
 * @param {{isAborted?: () => boolean}} [options] isAborted is polled each tick
 * @returns {Promise<boolean>} true once mounted, false if aborted or timed out
 */
export const waitForSmartChartAnchors = async ({ isAborted } = {}) => {
  for (
    let waited = 0;
    waited < ANCHOR_WAIT_TIMEOUT_MS;
    waited += ANCHOR_POLL_INTERVAL_MS
  ) {
    if (isAborted && isAborted()) {
      return false;
    }
    if (
      document.querySelector('#smart-chart-tabpanel') &&
      document.querySelector(MOUNTED_ANCHOR_SELECTOR)
    ) {
      return true;
    }
    await new Promise((resolve) => setTimeout(resolve, ANCHOR_POLL_INTERVAL_MS));
  }
  console.warn(
    'Smart Chart anchors never mounted; building the tutorial slides from whatever is present'
  );
  return false;
};

/**
 * Builds the Smart Chart tutorial section for the currently open Smart Chart
 * window, filtered to slides whose anchors exist for this patient.
 * The Smart Assist slides are still feature-flagged downstream, so callers
 * that include them must pass the section as expectedSteps, not a count.
 * @param {{includeSmartAssist?: boolean}} [options]
 * @returns {{id: string, steps: Array}|null} section, or null if unavailable
 */
export const buildSmartChartSection = ({ includeSmartAssist = false } = {}) => {
  const dashboardSection = tutorialConfig.sections.find(
    (section) => section.id === 'dashboard_intro'
  );
  const stepIds = includeSmartAssist
    ? [...SMART_CHART_STEP_IDS, ...SMART_CHART_SMART_ASSIST_STEP_IDS]
    : SMART_CHART_STEP_IDS;
  const steps = (dashboardSection?.steps || []).filter((step) => {
    if (!stepIds.includes(step.id)) {
      return false;
    }
    const requiredSelector = STEP_REQUIRED_SELECTORS[step.id];
    return !requiredSelector || Boolean(document.querySelector(requiredSelector));
  });
  return steps.length > 0 ? { id: 'dashboard_intro', steps } : null;
};
